import { logRocketApiKey } from '@bcf-vanilla-ts-v1-configurators/tokens/log-rocket/tikal-casino/log-rocket-api-key';
import { registerLogRocketApiKey } from '@bcf-vanilla-ts-v1-configurators/tokens/log-rocket/token';
import { domainNumberStrExtractor } from '@bcf-vanilla-ts-v1-platforms/platform-worker/common/domain-number-extractor';
import { DOMAIN_NUMBER_STR } from '@bcf-vanilla-ts-v1-platforms/platform-worker/common/domain-number-str';
import { provideDiTokenValue, registerDiToken } from '@bcf-vanilla-ts-v1-shared/di/di-token';
import type { DeviceInfoPayload } from '@bcf-vanilla-ts-v1-utilities/device-info/types';
import { extractDevice } from '@bcf-vanilla-ts-v1-utilities/device-info/utils';
import { registerPwaDeviceContext } from '@bcf-vanilla-ui-ts-v1/shared/pwa/pwa-device-context';
import { registerEnvBase } from '@bcf2-envs/env-token';
import {
  campaignIdsWithExpirationDate,
  superiorIdsWithExpirationDate
} from '@bcf2-v1-configurators-tokens/affiliate/tikal-casino/affiliate-ids-with-expiration-date';
import {
  CAMPAIGN_IDS_WITH_EXPIRATION_DATE,
  SUPERIOR_IDS_WITH_EXPIRATION_DATE
} from '@bcf2-v1-configurators-tokens/affiliate/token';
import { appMobileCasinoCountryBlocker } from '@bcf2-v1-configurators-tokens/app-country-blocker/tikal-casino/app-country-blocker';
import { APP_COUNTRY_BLOCKER } from '@bcf2-v1-configurators-tokens/app-country-blocker/token';
import { APP_HOSTNAME, APP_RELEASE_VERSION } from '@bcf2-v1-configurators-tokens/app/token';
import { gamesImagesAssetsUrl } from '@bcf2-v1-configurators-tokens/casino/tikal-casino/games-images-assets-url';
import { GAMES_IMAGES_ASSETS_URL } from '@bcf2-v1-configurators-tokens/casino/token';
import { defaultLangRedirectorCustomCountryLangMap } from '@bcf2-v1-configurators-tokens/default-lang-redirector/tikal-casino/default-lang-redirector-custom-country-lang.map';
import { DEFAULT_LANG_REDIRECTOR_CUSTOM_COUNTRY_LANG_MAP } from '@bcf2-v1-configurators-tokens/default-lang-redirector/token';
import { FOOTER_LICENSE_CUSTOM_COMPONENT } from '@bcf2-v1-configurators-tokens/footer-license/token';
import { footerLinks } from '@bcf2-v1-configurators-tokens/footer-links/tikal-casino/footer-links';
import { FOOTER_LINKS } from '@bcf2-v1-configurators-tokens/footer-links/token';
import { imgWebConfig } from '@bcf2-v1-configurators-tokens/img-webp/tikal-casino/img-web-config';
import { IMG_WEB_CONFIG } from '@bcf2-v1-configurators-tokens/img-webp/token';
import { casinoMobileOptiLitswRegistrationConfig } from '@bcf2-v1-configurators-tokens/litsw-registration/litsw-registration-presets';
import { LITSW_REGISTRATION_CONFIG } from '@bcf2-v1-configurators-tokens/litsw-registration/token';
import {
  OPTIMOVE_SDK_CONFIG_LOADER,
  OPTIMOVE_WRAPPER_STYLES_LOADER
} from '@bcf2-v1-configurators-tokens/optimove-sdk/tokens';
import { OptimoveSdkConfigs } from '@bcf2-v1-configurators-tokens/optimove-sdk/types';
import { piqStyles } from '@bcf2-v1-configurators-tokens/piq-styles/tikal/piq-styles';
import { piqTheme } from '@bcf2-v1-configurators-tokens/piq-styles/tikal/piq-theme';
import { PIQ_CSS, PIQ_THEME } from '@bcf2-v1-configurators-tokens/piq-styles/token';
import { sportsLink } from '@bcf2-v1-configurators-tokens/sports-links/tikal-casino/sports-links';
import { SPORTSBOOK_FULL_URL } from '@bcf2-v1-configurators-tokens/sports-links/token';
import { wizardPostFormHiddenFieldsWithoutPromocode } from '@bcf2-v1-configurators-tokens/wizard-post/curacao/wizard-form-post-hidden-fields';
import { cpWizardPostFormLayoutConfig } from '@bcf2-v1-configurators-tokens/wizard-post/curacao/wizard-post-form-layout-config';
import {
  CP_WIZARD_POST_FORM_LAYOUT_CONFIG,
  WIZARD_POST_FORM_HIDDEN_FIELDS
} from '@bcf2-v1-configurators-tokens/wizard-post/token';
import { configureSvgIcons } from '@bcfds-design-system/components/svg-icon/configurator';
import { envCommonToLit20EnvBase } from '@bcflit-env/env-converter';
import { EnvCommon } from '@bcflit-env/types';
import { releaseVersion } from '@bcflit-release-version';
import { APP_WITH_WORKER } from '@bcflit-v1-configurators/tokens/app/token';
import { USER_PANEL_DISPLAY_TIME_IN_LOCAL_TIME_ZONE } from '@bcflit-v1-configurators/tokens/date-format/token';
import { VERIFICATION_I18N_LOADER } from '@bcflit-v1-configurators/tokens/desc/verification/token';
import { I18nVerificationDesc } from '@bcflit-v1-configurators/tokens/desc/verification/types';
import { CSSResultGroup, TemplateResult } from 'lit';

export function registerSettingsProviders(envCommon: EnvCommon): void {
  registerDiToken(APP_RELEASE_VERSION, () => releaseVersion);
  registerEnvBase(envCommonToLit20EnvBase(envCommon));
  const deviceInfo: DeviceInfoPayload = extractDevice('mobile-web', window, navigator);
  registerPwaDeviceContext({
    isMobile: () => deviceInfo.isMobile,
    isPWA: () => deviceInfo.isPwa,
    isIosDevice: () => deviceInfo.isIos,
    isSafari: () => deviceInfo.isSafari
  });
  registerDiToken(DOMAIN_NUMBER_STR, () => domainNumberStrExtractor(provideDiTokenValue(APP_HOSTNAME)));
  registerDiToken(DEFAULT_LANG_REDIRECTOR_CUSTOM_COUNTRY_LANG_MAP, () => defaultLangRedirectorCustomCountryLangMap);
  registerDiToken(GAMES_IMAGES_ASSETS_URL, () => gamesImagesAssetsUrl);
  registerDiToken(IMG_WEB_CONFIG, () => imgWebConfig);
  registerDiToken(CP_WIZARD_POST_FORM_LAYOUT_CONFIG, () => cpWizardPostFormLayoutConfig);
  registerDiToken(WIZARD_POST_FORM_HIDDEN_FIELDS, () => wizardPostFormHiddenFieldsWithoutPromocode);
  registerDiToken(APP_WITH_WORKER, () => true);
  registerDiToken(
    VERIFICATION_I18N_LOADER,
    () => (): Promise<I18nVerificationDesc | undefined> =>
      // eslint-disable-next-line @typescript-eslint/typedef
      import('@bcflit-v1-configurators/tokens/desc/verification/tikal-casino/i18n-descs').then((mod) => mod.i18nDesc)
  );
  registerDiToken(PIQ_THEME, () => piqTheme);
  registerDiToken(PIQ_CSS, () => async (): Promise<string> => piqStyles);
  registerDiToken(APP_COUNTRY_BLOCKER, () => appMobileCasinoCountryBlocker);
  registerDiToken(FOOTER_LINKS, () => footerLinks);
  configureSvgIcons({
    domainNumberStr: domainNumberStrExtractor(window.location.hostname),
    svgIconConfig: envCommon.svgIconConfig
  });
  registerDiToken(USER_PANEL_DISPLAY_TIME_IN_LOCAL_TIME_ZONE, () => true);
  registerDiToken(
    FOOTER_LICENSE_CUSTOM_COMPONENT,
    () => (): Promise<TemplateResult> =>
      // eslint-disable-next-line @typescript-eslint/typedef
      import('@bcf2-v1-configurators-tokens/footer-license/tikal-casino/footer-license').then((mod) =>
        mod.tikalCasinoFooterLicense()
      )
  );
  registerDiToken(CAMPAIGN_IDS_WITH_EXPIRATION_DATE, () => campaignIdsWithExpirationDate);
  registerDiToken(SUPERIOR_IDS_WITH_EXPIRATION_DATE, () => superiorIdsWithExpirationDate);
  registerLogRocketApiKey(logRocketApiKey);
  registerDiToken(SPORTSBOOK_FULL_URL, () => sportsLink);
  registerDiToken(LITSW_REGISTRATION_CONFIG, () => casinoMobileOptiLitswRegistrationConfig);
  registerDiToken(
    OPTIMOVE_WRAPPER_STYLES_LOADER,
    () => (): Promise<CSSResultGroup> =>
      import('@bcf2-v1-configurators-tokens/optimove-sdk/tikal-casino/optimove-wrapper.mobile.styles').then(
        // eslint-disable-next-line @typescript-eslint/typedef
        (mod) => mod.optimoveWrapperMobileStyles
      )
  );
  registerDiToken(
    OPTIMOVE_SDK_CONFIG_LOADER,
    () => (): Promise<OptimoveSdkConfigs> =>
      import('@bcf2-v1-configurators-tokens/optimove-sdk/tikal-casino/casino/mobile-web/optimove-sdk-config').then(
        // eslint-disable-next-line @typescript-eslint/typedef
        (mod) => mod.optimoveSdkConfigs
      )
  );
}
